<template>
  <EnhancedDataTable
    ref="dataTable"
    title="Lines"
    :items="allLines || []"
    :headers="headers"
    item-key="Line_SK"
    selection-state-name="selectedLineSks"
    :loading="allLinesLoading ? 'info' : false"
    :initial-items-per-page="20"
    :footer-props="{ itemsPerPageOptions: [-1, 10, 15, 20, 25, 50, 100] }"
    show-select
    select-strategy="page"
    show-crud-actions
    show-reset-filters
    show-unselect-all
    entity-name="Line"
    @add="openDialog()"
    @edit="openDialog($event)"
    @delete="handleDelete($event)"
    @merge="openMergeDialog($event)"
    max-height="calc(100vh - 180px)"
  />

  <v-dialog v-model="dialog" max-width="500px" @keydown.esc="close" persistent>
    <v-card>
      <v-card-title>
        <span class="text-h5">{{ formTitle }}</span>
      </v-card-title>

      <v-card-text>
        <v-container grid-list-md>
          <v-row wrap>
            <v-col cols="12">
              <v-text-field v-model="editedItem.Line_Name" label="Line Name" autofocus></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-autocomplete
                :items="allBrands || []"
                item-title="Brand_Name"
                item-value="Brand_SK"
                v-model="editedItem.Brand_FK"
                label="Brand"
              ></v-autocomplete>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="save" class="pr-5">
          <v-icon start>mdi-content-save-outline</v-icon>Save
        </v-btn>
        <v-btn color="secondary" @click="close" class="pr-4">
          <v-icon start>mdi-close</v-icon>Cancel
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <MergeMasterDataDialog
    v-model="mergeDialog"
    :selected-records="selectedRecordsForMerge"
    :entity-name="'Lines'"
    item-key="Line_SK"
    display-field="Line_Name"
    :loading="mergeLoading"
    @merge="handleMerge"
  />

  <DeleteConfirmationDialog
    v-model="deleteDialog"
    entity-name="Line"
    :item-name="itemToDelete?.Line_Name"
    @confirm="confirmDelete"
  />
</template>

<script setup>
import { ref, computed, inject } from 'vue'
import { useStore } from 'vuex'
import { useMutation } from '@vue/apollo-composable'

import EnhancedDataTable from '@/components/EnhancedDataTable.vue'
import MergeMasterDataDialog from '@/components/MergeMasterDataDialog.vue'
import DeleteConfirmationDialog from '@/components/DeleteConfirmationDialog.vue'

import ADD_LINE from '@/graphql/Line/addLine.gql'
import UPDATE_LINE from '@/graphql/Line/updateLine.gql'
import REMOVE_LINE from '@/graphql/Line/removeLine.gql'
import MERGE_LINE from '@/graphql/Line/mergeLine.gql'

const store = useStore()

// Use App.vue's allLines data via inject (respects selection filtering)
const rc = inject('rc')
const allLines = computed(() => rc?.allLines || [])
const allLinesLoading = computed(() => rc?.$apollo?.queries?.allLines?.loading || false)

// Use App.vue's allBrands data
const allBrands = computed(() => rc?.allBrands || [])

const headers = [
  { title: '#', key: 'Line_SK', filterType: 'number', align: 'center' },
  { title: 'Brand', key: 'Brand.Brand_Name', filterType: 'combo', align: 'left' },
  { title: 'Line Name', key: 'Line_Name', filterType: 'text', align: 'left' },
  { title: 'Log', key: 'Mutation_Logs_Count', align: 'center', width: '60px' },
  { title: 'Actions', key: 'actions', sortable: false, align: 'center', width: '120px' }
]

const dataTable = ref(null)
const editedIndex = ref(-1)
const editedItem = ref({})
const formTitle = computed(() => editedIndex.value === -1 ? 'New Line' : 'Edit Line')
const dialog = ref(false)
const mergeDialog = ref(false)
const mergeLoading = ref(false)
const selectedRecordsForMerge = ref([])
const deleteDialog = ref(false)
const itemToDelete = ref(null)

const addError = (message) => store.commit('addError', message)
const addSuccess = (message) => store.commit('addSuccess', message)

const openDialog = (item = null) => {
  if (item) {
    editedIndex.value = allLines.value?.indexOf(item) ?? -1
    editedItem.value = { ...item }
  } else {
    editedIndex.value = -1
    editedItem.value = {}
  }
  dialog.value = true
}

const close = () => {
  dialog.value = false
  setTimeout(() => {
    editedItem.value = {}
    editedIndex.value = -1
  }, 300)
}

const { mutate: addLine } = useMutation(ADD_LINE, {
  update: (cache, { data: { addLine } }) => {
    // Add new line to the cached allLines array (works regardless of query variables)
    cache.modify({
      fields: {
        allLines(existingLines = [], { toReference }) {
          const newLineRef = toReference(addLine)
          return [...existingLines, newLineRef]
        }
      }
    })
    // Set initial mutation log count on the new line
    cache.modify({
      id: `Line:${addLine.Line_SK}`,
      fields: {
        Mutation_Logs_Count() {
          return 1
        }
      }
    })
  }
})

const { mutate: updateLine } = useMutation(UPDATE_LINE, {
  update: (cache, { data: { updateLine } }) => {
    cache.modify({
      id: `Line:${updateLine.Line_SK}`,
      fields: {
        Mutation_Logs_Count(count) {
          return (count || 0) + 1
        }
      }
    })
  }
})

const save = () => {
  const input = {
    Line_SK: editedItem.value.Line_SK,
    Line_Name: editedItem.value.Line_Name,
    Brand_FK: editedItem.value.Brand_FK
  }
  if (editedIndex.value > -1) {
    updateLine({ UpdateLineInput: input })
      .then(() => {
        close()
      })
      .catch((error) => addError(error.message))
  } else {
    const { Line_SK: _Line_SK, ...addInput } = input
    addLine({ AddLineInput: addInput })
      .then(() => {
        close()
      })
      .catch((error) => addError(error.message))
  }
}

const { mutate: removeLine } = useMutation(REMOVE_LINE)

const handleDelete = (item) => {
  itemToDelete.value = item
  deleteDialog.value = true
}

const confirmDelete = () => {
  const item = itemToDelete.value
  if (!item) return

  removeLine({
    Line_SK: item.Line_SK
  }, {
    update: (cache) => {
      cache.evict({ id: `Line:${item.Line_SK}` })
      cache.gc()
    }
  })
    .then(() => {
      // Remove deleted item from selection
      dataTable.value?.removeFromSelection(item.Line_SK)
    })
    .catch((error) => addError(error.message))
    .finally(() => {
      itemToDelete.value = null
    })
}

const openMergeDialog = (records) => {
  selectedRecordsForMerge.value = records
  mergeDialog.value = true
}

const { mutate: mergeLine } = useMutation(MERGE_LINE)

const handleMerge = (mergeData) => {
  mergeLoading.value = true
  mergeLine({
    MergeLineInput: {
      survivor_Line_SK: mergeData.survivor,
      duplicate_Line_SKs: mergeData.duplicates
    }
  }, {
    update: (cache, { data: { mergeLine } }) => {
      // Evict duplicates from cache
      mergeData.duplicates.forEach(id => {
        cache.evict({ id: `Line:${id}` })
      })
      cache.gc()
      // Increment survivor's mutation log count
      cache.modify({
        id: `Line:${mergeLine.Line_SK}`,
        fields: {
          Mutation_Logs_Count(count) {
            return (count || 0) + 1
          }
        }
      })
    }
  })
    .then(() => {
      // Remove merged duplicates from selection
      dataTable.value?.removeFromSelection(mergeData.duplicates)
      mergeDialog.value = false
      addSuccess(`Successfully merged ${mergeData.duplicates.length + 1} lines`)
    })
    .catch((error) => addError(error.message))
    .finally(() => {
      mergeLoading.value = false
    })
}
</script>
